<template>
  <div class="bg-white rounded-xl shadow-lg p-6">
    <h2 class="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
      <span>📊</span>
      Comparativa con Período Anterior
    </h2>

    <!-- Períodos -->
    <div class="grid grid-cols-2 gap-4 mb-6">
      <div class="bg-blue-50 rounded-lg p-3 border border-blue-200">
        <p class="text-sm text-gray-600 mb-1">Período Actual</p>
        <p class="text-sm font-semibold text-gray-900">
          {{ formatearFecha(comparativas.periodo_actual.inicio) }} - {{ formatearFecha(comparativas.periodo_actual.fin) }}
        </p>
      </div>
      <div class="bg-gray-50 rounded-lg p-3 border border-gray-200">
        <p class="text-sm text-gray-600 mb-1">Período Anterior</p>
        <p class="text-sm font-semibold text-gray-900">
          {{ formatearFecha(comparativas.periodo_anterior.inicio) }} - {{ formatearFecha(comparativas.periodo_anterior.fin) }}
        </p>
      </div>
    </div>

    <!-- Resumen Comparativo -->
    <div :class="getEvaluacionClass(comparativas.resumen_comparativo.evaluacion_general)" class="rounded-lg p-4 mb-6">
      <div class="flex items-center gap-3">
        <span class="text-3xl">{{ getEvaluacionIcono(comparativas.resumen_comparativo.evaluacion_general) }}</span>
        <div>
          <h3 class="font-semibold text-gray-900">{{ comparativas.resumen_comparativo.evaluacion_general }}</h3>
          <p class="text-sm text-gray-700">
            {{ comparativas.resumen_comparativo.mejoro }} métricas mejoraron, 
            {{ comparativas.resumen_comparativo.empeoro }} empeoraron
          </p>
        </div>
      </div>
    </div>

    <!-- Variaciones -->
    <div class="space-y-4">
      <!-- Ventas -->
      <div class="border rounded-lg p-4">
        <h4 class="font-semibold text-gray-900 mb-3">Total de Ventas</h4>
        <div class="grid grid-cols-3 gap-4 mb-3">
          <div>
            <p class="text-xs text-gray-600">Anterior</p>
            <p class="text-xl font-bold text-gray-700">{{ comparativas.variaciones.ventas.anterior }}</p>
          </div>
          <div>
            <p class="text-xs text-gray-600">Actual</p>
            <p class="text-xl font-bold text-blue-600">{{ comparativas.variaciones.ventas.actual }}</p>
          </div>
          <div>
            <p class="text-xs text-gray-600">Diferencia</p>
            <div class="flex items-center gap-2">
              <span class="text-xl font-bold" :class="getTendenciaColor(comparativas.variaciones.ventas.tendencia)">
                {{ comparativas.variaciones.ventas.diferencia > 0 ? '+' : '' }}{{ comparativas.variaciones.ventas.diferencia }}
              </span>
              <span class="text-xs">{{ getTendenciaIcono(comparativas.variaciones.ventas.tendencia) }}</span>
            </div>
            <p class="text-xs text-gray-600">{{ comparativas.variaciones.ventas.porcentaje.toFixed(1) }}%</p>
          </div>
        </div>
      </div>

      <!-- Valor Ventas -->
      <div class="border rounded-lg p-4">
        <h4 class="font-semibold text-gray-900 mb-3">Valor de Ventas</h4>
        <div class="grid grid-cols-3 gap-4 mb-3">
          <div>
            <p class="text-xs text-gray-600">Anterior</p>
            <p class="text-lg font-bold text-gray-700">{{ formatearMoneda(comparativas.variaciones.valor_ventas.anterior) }}</p>
          </div>
          <div>
            <p class="text-xs text-gray-600">Actual</p>
            <p class="text-lg font-bold text-blue-600">{{ formatearMoneda(comparativas.variaciones.valor_ventas.actual) }}</p>
          </div>
          <div>
            <p class="text-xs text-gray-600">Diferencia</p>
            <div class="flex items-center gap-2">
              <span class="text-lg font-bold" :class="getTendenciaColor(comparativas.variaciones.valor_ventas.tendencia)">
                {{ comparativas.variaciones.valor_ventas.diferencia > 0 ? '+' : '' }}{{ formatearMoneda(comparativas.variaciones.valor_ventas.diferencia) }}
              </span>
              <span class="text-xs">{{ getTendenciaIcono(comparativas.variaciones.valor_ventas.tendencia) }}</span>
            </div>
            <p class="text-xs text-gray-600">{{ comparativas.variaciones.valor_ventas.porcentaje.toFixed(1) }}%</p>
          </div>
        </div>
      </div>

      <!-- Tasa Producción -->
      <div class="border rounded-lg p-4">
        <h4 class="font-semibold text-gray-900 mb-3">Tasa de Producción</h4>
        <div class="grid grid-cols-3 gap-4">
          <div>
            <p class="text-xs text-gray-600">Anterior</p>
            <p class="text-xl font-bold text-gray-700">{{ comparativas.variaciones.tasa_produccion.anterior.toFixed(1) }}%</p>
          </div>
          <div>
            <p class="text-xs text-gray-600">Actual</p>
            <p class="text-xl font-bold text-blue-600">{{ comparativas.variaciones.tasa_produccion.actual.toFixed(1) }}%</p>
          </div>
          <div>
            <p class="text-xs text-gray-600">Diferencia</p>
            <div class="flex items-center gap-2">
              <span class="text-xl font-bold" :class="comparativas.variaciones.tasa_produccion.mejora ? 'text-green-600' : 'text-red-600'">
                {{ comparativas.variaciones.tasa_produccion.diferencia_puntos > 0 ? '+' : '' }}{{ comparativas.variaciones.tasa_produccion.diferencia_puntos.toFixed(1) }}
              </span>
              <span class="text-xl">{{ comparativas.variaciones.tasa_produccion.mejora ? '✅' : '⚠️' }}</span>
            </div>
            <p class="text-xs text-gray-600">puntos</p>
          </div>
        </div>
      </div>

      <!-- Tasa Instalación -->
      <div class="border rounded-lg p-4">
        <h4 class="font-semibold text-gray-900 mb-3">Tasa de Instalación</h4>
        <div class="grid grid-cols-3 gap-4">
          <div>
            <p class="text-xs text-gray-600">Anterior</p>
            <p class="text-xl font-bold text-gray-700">{{ comparativas.variaciones.tasa_instalacion.anterior.toFixed(1) }}%</p>
          </div>
          <div>
            <p class="text-xs text-gray-600">Actual</p>
            <p class="text-xl font-bold text-blue-600">{{ comparativas.variaciones.tasa_instalacion.actual.toFixed(1) }}%</p>
          </div>
          <div>
            <p class="text-xs text-gray-600">Diferencia</p>
            <div class="flex items-center gap-2">
              <span class="text-xl font-bold" :class="comparativas.variaciones.tasa_instalacion.mejora ? 'text-green-600' : 'text-red-600'">
                {{ comparativas.variaciones.tasa_instalacion.diferencia_puntos > 0 ? '+' : '' }}{{ comparativas.variaciones.tasa_instalacion.diferencia_puntos.toFixed(1) }}
              </span>
              <span class="text-xl">{{ comparativas.variaciones.tasa_instalacion.mejora ? '✅' : '⚠️' }}</span>
            </div>
            <p class="text-xs text-gray-600">puntos</p>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'Comparativas',
  props: {
    comparativas: {
      type: Object,
      required: true
    }
  },
  methods: {
    formatearFecha(fecha) {
      const date = new Date(fecha + 'T00:00:00');
      return date.toLocaleDateString('es-MX', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    },
    formatearMoneda(valor) {
      return new Intl.NumberFormat('es-MX', {
        style: 'currency',
        currency: 'MXN',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
      }).format(valor);
    },
    getEvaluacionClass(evaluacion) {
      switch (evaluacion) {
        case 'POSITIVO':
          return 'bg-green-100 border-2 border-green-300';
        case 'NEUTRAL':
          return 'bg-yellow-100 border-2 border-yellow-300';
        case 'NEGATIVO':
          return 'bg-red-100 border-2 border-red-300';
        default:
          return 'bg-gray-100 border-2 border-gray-300';
      }
    },
    getEvaluacionIcono(evaluacion) {
      switch (evaluacion) {
        case 'POSITIVO':
          return '✅';
        case 'NEUTRAL':
          return '⚠️';
        case 'NEGATIVO':
          return '❌';
        default:
          return '⚪';
      }
    },
    getTendenciaColor(tendencia) {
      return tendencia === 'SUBIENDO' ? 'text-green-600' : tendencia === 'BAJANDO' ? 'text-red-600' : 'text-gray-600';
    },
    getTendenciaIcono(tendencia) {
      return tendencia === 'SUBIENDO' ? '📈' : tendencia === 'BAJANDO' ? '📉' : '➡️';
    }
  }
};
</script>
